import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	ReactNode,
} from 'react';
import { useLoad } from './sw.hooks';
import { Logger } from '@shared/lib/logger';

const logger = new Logger('sw-context');

// Only external content images (CDN-hosted article/issue art), not our own
// same-origin app-shell assets -- those are already Workbox-precached.
//
// Deliberately not filtering by URL extension: this only runs against real
// <img> elements already rendered in the DOM (see collectAndSend below), so
// "is this an image" is already established by the DOM itself. A CDN that
// serves images via extensionless or query-string-based URLs (resize/format
// params, no trailing .jpg/.png) would silently never get forwarded to the
// SW for caching if we re-validated the URL shape here.
function isExternalContentImage(src: string): boolean {
	try {
		const url = new URL(src, window.location.href);
		if (url.protocol !== 'http:' && url.protocol !== 'https:') {
			return false;
		}
		return url.origin !== window.location.origin;
	} catch {
		return false;
	}
}

export type ServiceWorkerMessage =
	| {
			type: 'DOWNLOAD_BACKGROUND_IMAGES';
			payload: {
				images: Array<string>;
				IssueID?: string | number;
				priority?: 'high' | 'low';
				// Per-URL issue ownership, for batches spanning more than one
				// issue (e.g. a catalogue page's covers) where a single
				// top-level IssueID can't express it. Keyed by the same
				// string that appears in `images`. Entries with no owner
				// here and no top-level IssueID are cached but untracked --
				// evictIssue() can't reclaim them individually, only a full
				// cache wipe does.
				imageOwners?: Record<string, string>;
			};
	  }
	| {
			type: 'DOWNLOAD_BACKGROUND_REPLICA_PAGES';
			payload: {
				pages: Array<string>;
				IssueID?: string | number;
			};
	  }
	| {
			// Deletes every cached asset (image/audio/video) attributed to
			// one issue, without touching any other issue's downloads or the
			// shared fonts/assets caches. Not yet called from any UI -- the
			// mechanism this exists for (a real eviction policy, and
			// removing an un-favorited issue's cache alongside its Dexie
			// row) is separate, still-open DASHBOARD.md work; this message
			// is the prerequisite capability those need.
			type: 'EVICT_ISSUE';
			payload: {
				IssueID: string | number;
			};
	  };

export interface ServiceWorkerContextType {
	serviceWorker: ServiceWorker | null;
	isReady: boolean;
	sendMessage: (message: ServiceWorkerMessage) => void;
	// Convenience wrapper around the EVICT_ISSUE message -- deletes one
	// issue's cached images/audio/video without touching any other issue or
	// the shared fonts/assets caches. Not called anywhere yet; exists so the
	// still-open eviction-policy and un-favorite-cleanup work (DASHBOARD.md
	// Section 02) has the capability ready rather than needing to add it.
	evictIssue: (issueId: string | number) => void;
}

export const ServiceWorkerContext = createContext<
	ServiceWorkerContextType | undefined
>(undefined);

export const useServiceWorkerContext = () => {
	const context = useContext(ServiceWorkerContext);
	if (context === undefined) {
		throw new Error(
			'useServiceWorkerContext must be used within a ServiceWorkerProvider',
		);
	}
	return context;
};

interface ServiceWorkerProviderProps {
	children: ReactNode;
}

export const ServiceWorkerProvider: React.FC<ServiceWorkerProviderProps> = ({
	children,
}) => {
	const serviceWorker = useLoad();
	const isReady = serviceWorker !== null;

	const sendMessage = useCallback(
		(message: ServiceWorkerMessage) => {
			if (serviceWorker) {
				serviceWorker.postMessage(message);
			} else {
				logger.warn(
					'Service Worker not available to send message:',
					message,
				);
			}
		},
		[serviceWorker],
	);

	const evictIssue = useCallback(
		(issueId: string | number) => {
			sendMessage({ type: 'EVICT_ISSUE', payload: { IssueID: issueId } });
		},
		[sendMessage],
	);

	const value: ServiceWorkerContextType = {
		serviceWorker,
		isReady,
		sendMessage,
		evictIssue,
	};

	// Not every template's data hook explicitly pushes its images to the SW
	// for background caching (only a couple do, and only for cover/featured
	// thumbnails, not general article feeds) -- and passive caching via the
	// SW's own CacheFirst image route only works once the SW is actually
	// controlling the page, which it structurally isn't yet for the first
	// visit's initial navigation (a worker can't retroactively intercept the
	// load that triggered its own registration). Together those two gaps
	// meant most rendered content images were never available offline, even
	// right after being viewed. Generically scan the DOM for rendered <img>
	// src values instead, template-agnostic, and forward new ones to the SW
	// -- postMessage works regardless of controller/interception state, so
	// this covers the first-visit window too.
	const sentImagesRef = useRef<Set<string>>(new Set());
	useEffect(() => {
		if (!isReady) return;

		const collectAndSend = () => {
			const images = Array.from(document.images)
				.map((img) => img.currentSrc || img.src)
				.filter(
					(src) =>
						isExternalContentImage(src) &&
						!sentImagesRef.current.has(src),
				);

			if (!images.length) return;
			for (const src of images) {
				sentImagesRef.current.add(src);
			}
			sendMessage({
				type: 'DOWNLOAD_BACKGROUND_IMAGES',
				// These images are rendered in the DOM right now, unlike the
				// data-driven prefetch hooks (use-issue.ts, per-template
				// Home.hooks.ts, etc.) that send the same message type for
				// covers/thumbnails regardless of viewport — the SW's shared
				// download queue (src/sw.ts) uses this to let on-screen
				// images jump ahead of background prefetch.
				payload: { images, priority: 'high' },
			});
		};

		collectAndSend();

		let debounceId: ReturnType<typeof setTimeout> | undefined;
		const observer = new MutationObserver(() => {
			if (debounceId) clearTimeout(debounceId);
			debounceId = setTimeout(collectAndSend, 1500);
		});
		observer.observe(document.body, {
			childList: true,
			subtree: true,
			// srcset alongside src: a responsive-image component can swap
			// resolutions (e.g. a lazy-loading component picking a new size)
			// without ever touching the src attribute itself. New elements are
			// still caught via childList regardless of which attribute they
			// arrive with set.
			attributeFilter: ['src', 'srcset'],
		});

		return () => {
			observer.disconnect();
			if (debounceId) clearTimeout(debounceId);
		};
	}, [isReady, sendMessage]);

	return (
		<ServiceWorkerContext.Provider value={value}>
			{children}
		</ServiceWorkerContext.Provider>
	);
};
